import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, ReferenceLine, LineChart } from "recharts";
import { Calculator, Target, Calendar, TrendingUp, TrendingDown } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function BEP() {
  const { data, isLoading } = useQuery({
    queryKey: ["bep"],
    queryFn: () => api.get("/admin/bep").then((r) => r.data),
  });

  if (isLoading || !data) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Break Even Point</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-3">
          <Target className="w-7 h-7 text-orange-600" /> Punto di pareggio
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          BEP in fatturato = Costi Fissi / (1 − Costi Variabili / Fatturato)
          <span className="font-mono ml-2 text-slate-500">
            ({eur(data.costi_fissi_annuali)} ÷ (1 − {eur(data.costi_variabili)} / {eur(data.ricavi_previsti)}))
          </span>
        </p>
      </div>

      {/* Metriche principali */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard label="Costi di gestione annuali" value={eur(data.costi_fissi_annuali)} />
        <StatCard label="Costi variabili" value={eur(data.costi_variabili)} />
        <StatCard label="Fatturato previsto" value={eur(data.ricavi_previsti)} />
        <StatCard label="Margine contribuzione" value={eur(data.margine_contribuzione)} />
        <StatCard label="Ricavo medio / cantiere" value={eur(data.ricavo_medio_cantiere)} />
        <StatCard label="Margine medio %" value={`${data.margine_medio_pct}%`} highlight />
      </div>

      {/* Card BEP grande */}
      <div className="bg-white border border-slate-200 p-6">
        <div className="grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="overline">Fatturato minimo (BEP annuo)</div>
            <div className="font-display text-5xl xl:text-6xl font-black text-orange-600 mt-1">
              {eur(data.bep_fatturato)}
            </div>
            <div className="text-xs text-slate-500 mt-2 font-mono">
              Codice contabile di riferimento: <b>70.01</b> (Ricavi commesse) vs <b>60.xx + 62.xx</b>
            </div>
          </div>
          <div className="space-y-3">
            <Row label="Fatturato effettivo" value={eur(data.fatturato_effettivo)} />
            <Row label="Copertura raggiunta" value={`${data.copertura_pct}%`} strong={data.copertura_pct >= 100} />
            <Row
              label={data.distanza_dal_bep <= 0 ? "Superato di" : "Distanza dal BEP"}
              value={eur(Math.abs(data.distanza_dal_bep))}
              strong={data.distanza_dal_bep <= 0}
            />
            <Row label="Fatturato ancora necessario" value={eur(data.fatturato_ancora_necessario)} />
          </div>
        </div>
      </div>

      {/* Data prevista di raggiungimento & confronto BEP prev vs reale */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-2 flex items-center gap-2"><Calendar className="w-4 h-4" /> Data prevista di raggiungimento BEP</div>
          <div className="font-display text-3xl font-black">
            {data.data_prevista_bep === "già raggiunto" ? (
              <span className="text-emerald-700">Già raggiunto</span>
            ) : data.data_prevista_bep === "non stimabile" ? (
              <span className="text-slate-400">Non stimabile</span>
            ) : (
              <span>{new Date(data.data_prevista_bep).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-2 font-mono">
            Fatturato medio mensile: {eur(data.fatturato_medio_mensile)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-2 flex items-center gap-2"><Calculator className="w-4 h-4" /> BEP previsto vs reale</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-slate-500">Previsto</div>
              <div className="font-display font-black text-xl">{eur(data.bep_fatturato)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Reale (costi effettivi)</div>
              <div className="font-display font-black text-xl">{eur(data.bep_reale)}</div>
            </div>
          </div>
          <div className={`mt-3 text-sm font-mono ${data.scostamento_bep_pct > 5 ? "text-red-700" : data.scostamento_bep_pct < -5 ? "text-emerald-700" : "text-slate-700"}`}>
            {data.scostamento_bep_pct > 0 ? "+" : ""}{data.scostamento_bep_pct}% scostamento
            {data.scostamento_bep_pct > 5 && " (BEP reale peggiore del previsto)"}
            {data.scostamento_bep_pct < -5 && " (BEP reale migliore del previsto)"}
          </div>
        </div>
      </div>

      {/* Grafico BEP con aree */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="overline mb-3">Grafico Break Even (aree utile / perdita)</div>
        <div className="w-full h-96">
          <ResponsiveContainer>
            <ComposedChart data={data.chart}>
              <defs>
                <linearGradient id="colorLoss" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#dc2626" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#dc2626" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16a34a" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#16a34a" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="fatturato" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} label={{ value: "Fatturato €", position: "insideBottom", offset: -5, fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => (v == null ? "-" : eur(v))} labelFormatter={(v) => `Fatturato: ${eur(v)}`} />
              <Legend />
              <Area type="monotone" dataKey="area_perdita" fill="url(#colorLoss)" stroke="none" name="Area di perdita" />
              <Area type="monotone" dataKey="area_utile" fill="url(#colorProfit)" stroke="none" name="Area di utile" />
              <Line type="monotone" dataKey="costi_fissi" stroke="#dc2626" strokeWidth={2} dot={false} name="Costi di gestione" />
              <Line type="monotone" dataKey="costi_totali" stroke="#0f172a" strokeWidth={2} dot={false} name="Costi totali" />
              <Line type="monotone" dataKey="ricavi" stroke="#16a34a" strokeWidth={2} dot={false} name="Ricavi" />
              <ReferenceLine x={data.bep_fatturato} stroke="#ea580c" strokeDasharray="5 5" label={{ value: `BEP ${eur(data.bep_fatturato)}`, position: "top", fill: "#ea580c", fontSize: 11 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Andamento mensile cumulato */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="overline mb-3">Andamento mensile: ricavi cumulati vs BEP</div>
        {data.andamento_mensile.length === 0 ? (
          <div className="text-slate-400 text-sm">Nessun dato mensile disponibile.</div>
        ) : (
          <div className="w-full h-72">
            <ResponsiveContainer>
              <LineChart data={data.andamento_mensile}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => eur(v)} />
                <Legend />
                <Line type="monotone" dataKey="ricavi_cumulati" stroke="#16a34a" strokeWidth={2} name="Ricavi cumulati" />
                <Line type="monotone" dataKey="costi_variabili_cumulati" stroke="#ea580c" strokeWidth={2} name="Costi variabili cumulati" />
                <ReferenceLine y={data.bep_fatturato} stroke="#dc2626" strokeDasharray="5 5" label={{ value: "BEP annuo", position: "insideTopRight", fill: "#dc2626", fontSize: 10 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500 shadow-brutal" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-lg xl:text-xl mt-1">{value}</div>
    </div>
  );
}
function Row({ label, value, strong }) {
  return (
    <div className="flex justify-between items-center pb-2 border-b border-slate-100 last:border-0">
      <div className="text-sm text-slate-600">{label}</div>
      <div className={`font-mono ${strong ? "font-display font-black text-lg text-emerald-700" : "font-semibold"}`}>{value}</div>
    </div>
  );
}
